import { useCallback, useMemo } from 'react';
import {
  useCommentCreateMutation,
  useCommentDeleteMutation,
  useCommentReopenMutation,
  useCommentResolveMutation,
  useCommentUpdateMutation,
  useGetCommentsQuery,
  type GetCommentsQueryData,
} from '@/generated/graphql';

// Stable reference so consumers' memo deps don't churn while data is absent.
const EMPTY: GetCommentsQueryData['comments'] = [];

/**
 * Wraps the comments query + all 5 mutations for a project's track. The query
 * returns every version's threads (callers split by versionNumber); creating
 * a top-level comment stamps `versionNumber`. Every mutation refetches
 * GET_COMMENTS afterwards (a refetch failure is swallowed so a successful
 * mutation never looks failed).
 */
export function useTrackComments(args: {
  projectId: string;
  versionNumber: number;
  skip?: boolean;
}) {
  // The query omits versionNumber so every version shares one cache entry.
  const target = useMemo(
    () => ({
      targetType: 'PROJECT' as const,
      targetId: args.projectId,
      projectId: args.projectId,
    }),
    [args.projectId]
  );
  const query = useGetCommentsQuery({
    variables: { target },
    skip: args.skip,
    fetchPolicy: 'cache-and-network',
  });
  const [createMutation] = useCommentCreateMutation();
  const [updateMutation] = useCommentUpdateMutation();
  const [removeMutation] = useCommentDeleteMutation();
  const [resolveMutation] = useCommentResolveMutation();
  const [reopenMutation] = useCommentReopenMutation();

  const { refetch } = query;
  // Await the mutation first (its errors propagate), then refresh the list.
  // A failed refetch must not reject: the mutation already succeeded, and a
  // rejection would make the UI keep the text and the user retry (duplicate).
  const withRefresh = useCallback(
    async <T>(run: () => Promise<T>): Promise<T> => {
      const res = await run();
      try {
        await refetch();
      } catch (e) {
        console.warn('Comments refetch failed after successful mutation', e);
      }
      return res;
    },
    [refetch]
  );

  const create = useCallback(
    (body: string, atMs: number) =>
      withRefresh(() =>
        createMutation({
          variables: {
            input: {
              target: { ...target, versionNumber: args.versionNumber },
              body,
              atMs,
            },
          },
        })
      ),
    [createMutation, target, args.versionNumber, withRefresh]
  );
  const reply = useCallback(
    (parentId: string, body: string) =>
      withRefresh(() =>
        createMutation({ variables: { input: { target, body, parentId } } })
      ),
    [createMutation, target, withRefresh]
  );
  const update = useCallback(
    (commentId: string, body: string) =>
      withRefresh(() =>
        updateMutation({ variables: { input: { commentId, body } } })
      ),
    [updateMutation, withRefresh]
  );
  const remove = useCallback(
    (commentId: string) =>
      withRefresh(() =>
        removeMutation({ variables: { input: { commentId } } })
      ),
    [removeMutation, withRefresh]
  );
  const resolve = useCallback(
    (commentId: string) =>
      withRefresh(() =>
        resolveMutation({ variables: { input: { commentId } } })
      ),
    [resolveMutation, withRefresh]
  );
  const reopen = useCallback(
    (commentId: string) =>
      withRefresh(() =>
        reopenMutation({ variables: { input: { commentId } } })
      ),
    [reopenMutation, withRefresh]
  );

  return {
    threads: query.data?.comments ?? EMPTY,
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
    create,
    reply,
    update,
    remove,
    resolve,
    reopen,
  };
}
